import React from 'react';
import { AlertTriangle, RefreshCw, Home } from 'lucide-react';

/**
 * Global Error Boundary Component
 * Catches React errors and provides fallback UI
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      errorCount: 0,
      children: props.children,
    };
  }

  static getDerivedStateFromProps(props, state) {
    if (state.hasError && props.children !== state.children) {
      return {
        hasError: false,
        error: null,
        errorInfo: null,
        children: props.children,
      };
    }

    return null;
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    const errorCount = this.state.errorCount + 1;

    this.setState({
      error,
      errorInfo,
      errorCount,
    });

    // Log to console in development
    if (process.env.NODE_ENV === 'development') {
      console.error('🚨 Error Boundary Caught:', error);
      console.error('📍 Error Info:', errorInfo);
    }

    // TODO: Send to error tracking service (Sentry, LogRocket, etc.)
    // this.logErrorToService(error, errorInfo);
  }

  logErrorToService = (error, errorInfo) => {
    // Integration point for error tracking services
    try {
      // Example: Sentry.captureException(error, { extra: errorInfo });
      console.log('📤 Error logged to service:', error.message);
    } catch (e) {
      console.error('Failed to log error:', e);
    }
  };

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  handleGoHome = () => {
    window.location.href = '/';
  };

  render() {
    if (this.state.hasError) {
      const { error, errorInfo, errorCount } = this.state;

      // Too many errors - something is seriously wrong
      if (errorCount > 3) {
        return (
          <div className="min-h-screen bg-gradient-to-br from-red-50 to-red-100 flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-lg w-full text-center">
              <div className="flex justify-center mb-6">
                <div className="p-4 bg-red-100 rounded-full">
                  <AlertTriangle className="w-16 h-16 text-red-600" />
                </div>
              </div>
              <h1 className="text-3xl font-bold text-gray-900 mb-4">
                Critical Error
              </h1>
              <p className="text-gray-600 mb-6">
                We're experiencing repeated errors. Please refresh the page or contact support if the problem persists.
              </p>
              <div className="space-y-3">
                <button
                  onClick={() => window.location.reload()}
                  className="w-full flex items-center justify-center gap-2 px-6 py-3 pf-btn-primary font-semibold rounded-lg"
                >
                  <RefreshCw className="w-5 h-5" />
                  Refresh Page
                </button>
                <button
                  onClick={this.handleGoHome}
                  className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-gray-200 text-gray-800 font-semibold rounded-lg hover:bg-gray-300 transition-all"
                >
                  <Home className="w-5 h-5" />
                  Go to Homepage
                </button>
              </div>
            </div>
          </div>
        );
      }

      // Standard error UI
      return (
        <div className="min-h-screen bg-[var(--pf-canvas)] flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-2xl w-full">
            <div className="flex items-start gap-4 mb-6">
              <div className="p-3 bg-red-100 rounded-full flex-shrink-0">
                <AlertTriangle className="w-8 h-8 text-red-600" />
              </div>
              <div className="flex-1">
                <h1 className="text-2xl font-bold text-gray-900 mb-2">
                  Oops! Something went wrong
                </h1>
                <p className="text-gray-600">
                  We've encountered an unexpected error. Don't worry, your data is safe. You can try the actions below.
                </p>
              </div>
            </div>

            {/* Error Details (Development Only) */}
            {process.env.NODE_ENV === 'development' && error && (
              <div className="mb-6 p-4 bg-gray-100 rounded-lg border border-gray-300">
                <h3 className="font-semibold text-gray-900 mb-2 text-sm">Error Details (Dev Only):</h3>
                <p className="text-red-600 font-mono text-xs mb-2 break-all">
                  {error.toString()}
                </p>
                {errorInfo && (
                  <details className="text-xs text-gray-600">
                    <summary className="cursor-pointer font-semibold hover:text-gray-900">
                      Stack Trace
                    </summary>
                    <pre className="mt-2 overflow-auto p-2 bg-white rounded border border-gray-200 text-[10px]">
                      {errorInfo.componentStack}
                    </pre>
                  </details>
                )}
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex gap-3 flex-wrap">
              <button
                onClick={this.handleReset}
                className="flex items-center gap-2 px-6 py-3 pf-btn-primary font-semibold rounded-lg shadow-lg"
              >
                <RefreshCw className="w-5 h-5" />
                Try Again
              </button>
              <button
                onClick={this.handleGoHome}
                className="flex items-center gap-2 px-6 py-3 bg-gray-200 text-gray-800 font-semibold rounded-lg hover:bg-gray-300 transition-all"
              >
                <Home className="w-5 h-5" />
                Go to Homepage
              </button>
              <button
                onClick={() => window.location.reload()}
                className="flex items-center gap-2 px-6 py-3 border-2 border-gray-300 text-gray-700 font-semibold rounded-lg hover:border-gray-400 hover:bg-gray-50 transition-all"
              >
                Reload Page
              </button>
            </div>

            {/* Help Text */}
            <div className="mt-6 pt-6 border-t border-gray-200">
              <p className="text-sm text-gray-500 text-center">
                If this problem persists, please contact support at{' '}
                <a href="mailto:support@PetsFostering.com" className="text-[var(--pf-accent)] hover:opacity-80 font-semibold">
                  support@PetsFostering.com
                </a>
              </p>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
